import React, { useState } from 'react';
import {
  AccessTime,
  AccountBalance,
  Add,
  Assignment,
  BugReport,
  Build,
  Business,
  CalendarToday,
  Cancel,
  Chat,
  CheckCircle,
  CloudUpload,
  CreditCard,
  Description,
  Download,
  Edit,
  Email,
  Event,
  Extension,
  Feedback,
  FolderOpen,
  Gavel,
  Handshake,
  HelpOutline,
  Home,
  InfoOutlined,
  LocationOn,
  Message,
  Notifications,
  Person,
  Phone,
  Receipt,
  Schedule,
  Settings,
  TaskAlt,
  Timeline,
  Timeline as TimelineIcon,
  Upload,
  VerifiedUser,
  Visibility,
  Warning,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Slide,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { addDays, format } from 'date-fns';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CONTRACT_FINANCING_CONTINGENCY } from '@/config/constants';
import {
  useCreateAddendumMutation,
  useGetAddendumsQuery,
} from '@/apis/extended/addendumApi';
import { useGetContractQuery } from '@/apis/extended/contractApi';
import { useSendMessageMutation } from '@/apis/extended/messageApi';
import { useGetContractPipelineQuery } from '@/apis/extended/pipelineApi';
import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';
import {
  calculateContractTimeline,
  formatTimelineDate,
  getDaysUntilDeadline,
  getOverdueCount,
  getTimelineProgress,
} from '@/utils/contractTimeline';
import CreateAddendumForm from '../../Addendum/CreateAddendumForm';
import ClosingCoordination from '../../PostAgreement/ClosingCoordination';
import CommunicationHub from '../../PostAgreement/CommunicationHub';
import DocumentCenter from '../../PostAgreement/DocumentCenter';
import InspectionProperty from '../../PostAgreement/InspectionProperty';
import TaskManagement from '../../PostAgreement/TaskManagement';

interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'completed' | 'pending' | 'overdue' | 'in_progress';
  type: 'inspection' | 'financing' | 'title' | 'closing' | 'repair' | 'other';
  assignedTo: 'buyer' | 'seller' | 'agent' | 'all';
  priority: 'high' | 'medium' | 'low';
  completedDate?: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'pending' | 'completed' | 'overdue';
  assignedTo: 'buyer' | 'seller' | 'agent';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Fade in={value === index} timeout={300}>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    </Fade>
  );
}

const AgentPropertyDashboard: React.FC = () => {
  const theme = useTheme();
  const { contractId } = useParams<{ contractId: string }>();
  const location = useLocation();

  const getUserRoleFromPath = (): 'buyer' | 'seller' | 'agent' => {
    const path = location.pathname;
    if (path.includes('/buyer/property-dashboard/')) return 'buyer';
    if (path.includes('/seller/property-dashboard/')) return 'seller';
    if (path.includes('/agent/property-dashboard/')) return 'agent';
    return 'agent';
  };

  const userRole = getUserRoleFromPath();
  const userName = userRole.charAt(0).toUpperCase() + userRole.slice(1);

  if (!contractId) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">No contract ID found</Alert>
      </Container>
    );
  }

  const [activeTab, setActiveTab] = useState(0);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [extensionReason, setExtensionReason] = useState('');
  const [requestedDays, setRequestedDays] = useState(7);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);

  const { data: contract, isLoading: contractLoading } =
    useGetContractQuery(contractId);
  const { data: addendumsData } = useGetAddendumsQuery(contractId);
  const { data: pipelineData } = useGetContractPipelineQuery(contractId, {
    skip: !contractId,
  });
  const [createAddendum] = useCreateAddendumMutation();
  const [sendMessage] = useSendMessageMutation();
  const [viewedAddendumId, setViewedAddendumId] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleExtensionRequest = async () => {
    if (!contractId || !extensionReason.trim() || requestedDays <= 0) {
      toast.error(
        'Please provide a reason and valid number of days for the extension.'
      );
      return;
    }

    try {
      const newDeadline = addDays(new Date(), requestedDays);
      await createAddendum({
        contractId,
        title: `Extension Request: ${requestedDays} days`,
        description: extensionReason,
        type: 'extension',
        createdBy: userRole,
        extensionDetails: {
          requestedExtensionDays: requestedDays,
          reason: extensionReason,
          newDeadline: format(newDeadline, 'yyyy-MM-dd'),
          affectedMilestones: [],
        },
      }).unwrap();
      toast.success('Extension request submitted');
      setExtensionDialogOpen(false);
      setExtensionReason('');
      setRequestedDays(7);
    } catch (error) {
      console.error('Failed to create extension request:', error);
      toast.error('Failed to submit extension request.');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Enter a message before sending.');
      return;
    }
    try {
      await sendMessage({
        contractId,
        toRole: 'buyer',
        message: newMessage.trim(),
      }).unwrap();
      toast.success('Message sent');
      setMessageDialogOpen(false);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Could not send message.');
    }
  };

  const getProgress = () => {
    if (!contract) return 0;
    return getTimelineProgress(timelineEvents);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ratified':
        return 'success';
      case 'negotiating':
        return 'warning';
      case 'draft':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ratified':
        return <CheckCircle />;
      case 'negotiating':
        return <Handshake />;
      case 'draft':
        return <Description />;
      case 'cancelled':
        return <Cancel />;
      default:
        return <InfoOutlined />;
    }
  };

  const timelineEvents = React.useMemo(() => {
    if (!contract) return [];
    return calculateContractTimeline(contract);
  }, [contract]);

  const tasks: Task[] = [
    {
      id: 'task1',
      title: 'Review Inspection Report',
      description:
        'Review the property inspection report and decide on repair requests.',
      dueDate: format(addDays(new Date(), 5), 'yyyy-MM-dd'),
      status: 'pending',
      assignedTo: 'buyer',
    },
    {
      id: 'task2',
      title: 'Submit Loan Application',
      description:
        'Complete and submit all necessary documents for loan application.',
      dueDate: format(addDays(new Date(), 2), 'yyyy-MM-dd'),
      status: 'overdue',
      assignedTo: 'buyer',
    },
    {
      id: 'task3',
      title: 'Coordinate Appraisal',
      description: 'Schedule and ensure appraisal is completed.',
      dueDate: format(addDays(new Date(), 10), 'yyyy-MM-dd'),
      status: 'pending',
      assignedTo: 'agent',
    },
  ];

  const overdueItems = React.useMemo(() => {
    return getOverdueCount(timelineEvents);
  }, [timelineEvents]);

  if (contractLoading) {
    return (
      <Container maxWidth="lg">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Slide direction="up" in={true} timeout={500}>
        <Box sx={{ py: 4 }}>
          {/* Header Card */}
          <GlassCard
            variant="bold"
            sx={{
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.mainColors.primary} 0%, ${theme.palette.mainColors.secondary} 100%)`,
              color: theme.palette.mainColors.white,
            }}
          >
            <CardHeader
              avatar={
                <Avatar
                  sx={{
                    bgcolor: theme.palette.mainColors.backgroundOverlay,
                    color: theme.palette.mainColors.white,
                  }}
                >
                  <Business />
                </Avatar>
              }
              title={
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ color: theme.palette.mainColors.white }}
                >
                  Agent Property Dashboard
                </Typography>
              }
              subheader={
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: theme.palette.mainColors.white, opacity: 0.9 }}
                  >
                    {contract?.sellerPropertyInfo?.propertyAddress ||
                      contract?.product?.address?.fullAddress ||
                      [
                        contract?.product?.address?.street,
                        contract?.product?.address?.city,
                        contract?.product?.address?.state,
                        contract?.product?.address?.zip,
                      ]
                        .filter(Boolean)
                        .join(', ') ||
                      'Property Address'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.mainColors.white, opacity: 0.7 }}
                  >
                    Contract ID: {contractId} | Agent: {userName}
                  </Typography>
                </Box>
              }
              action={
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Create Addendum">
                    <IconButton
                      onClick={() => setAddendumDialogOpen(true)}
                      sx={{ color: theme.palette.mainColors.white }}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send Message">
                    <IconButton
                      onClick={() => setMessageDialogOpen(true)}
                      sx={{ color: theme.palette.mainColors.white }}
                    >
                      <Badge badgeContent={2} color="error">
                        <Message />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Request Extension">
                    <IconButton
                      onClick={() => setExtensionDialogOpen(true)}
                      sx={{ color: theme.palette.mainColors.white }}
                    >
                      <Extension />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Notifications">
                    <IconButton sx={{ color: theme.palette.mainColors.white }}>
                      <Badge badgeContent={overdueItems.length} color="error">
                        <Notifications />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
          </GlassCard>

          {/* Status Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <GlassCard variant="medium" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: 'text.primary', fontWeight: 600 }}
                  >
                    Contract Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Chip
                      label={
                        contract?.contractStatus?.replace(/_/g, ' ') || 'N/A'
                      }
                      color={getStatusColor(contract?.contractStatus || '')}
                      icon={getStatusIcon(contract?.contractStatus || '')}
                      sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getProgress()}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mb: 1,
                      backgroundColor: alpha(theme.palette.common.black, 0.08),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: theme.palette.mainColors.green,
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Overall Progress: {Math.round(getProgress())}%
                  </Typography>
                </CardContent>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassCard variant="medium" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: 'text.primary', fontWeight: 600 }}
                  >
                    Key Dates
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Event
                          sx={{ color: theme.palette.mainColors.primary }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Offer Date"
                        secondary={
                          contract?.timeline?.offerGoodUntillDate
                            ? formatTimelineDate(
                                contract.timeline.offerGoodUntillDate
                              )
                            : 'N/A'
                        }
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'text.primary',
                            fontWeight: 600,
                          },
                          '& .MuiListItemText-secondary': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarToday
                          sx={{ color: theme.palette.mainColors.primary }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Closing Date"
                        secondary={
                          contract?.offerSpecifics?.closingDate
                            ? formatTimelineDate(
                                contract.offerSpecifics.closingDate
                              )
                            : 'N/A'
                        }
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'text.primary',
                            fontWeight: 600,
                          },
                          '& .MuiListItemText-secondary': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </GlassCard>
            </Grid>
            <Grid item xs={12}>
              {pipelineData?.data && (
                <GlassCard variant="subtle">
                  <CardContent>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: 'text.primary', fontWeight: 600 }}
                      >
                        Pipeline Status
                      </Typography>
                      <Chip
                        label={
                          pipelineData.data.stages.find((s) => s.isCurrent)
                            ?.label || pipelineData.data.currentStageKey
                        }
                        color="primary"
                        size="small"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={
                        pipelineData.data.stages.find((s) => s.isCurrent)
                          ?.progress.percent ?? 0
                      }
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mb: 1,
                        backgroundColor: alpha(
                          theme.palette.common.black,
                          0.08
                        ),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.mainColors.primary,
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      Blockers:{' '}
                      {pipelineData.data.stages.find((s) => s.isCurrent)
                        ?.progress.blockers.length ?? 0}
                    </Typography>
                  </CardContent>
                </GlassCard>
              )}
            </Grid>
          </Grid>

          {/* Main Content Tabs */}
          <GlassCard variant="medium">
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="dashboard tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 64,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              >
                <Tab
                  label="Timeline"
                  icon={<TimelineIcon />}
                  iconPosition="start"
                />
                <Tab label="Tasks" icon={<Assignment />} iconPosition="start" />
                <Tab
                  label="Documents"
                  icon={<Description />}
                  iconPosition="start"
                />
                <Tab
                  label="Communication"
                  icon={<Chat />}
                  iconPosition="start"
                />
                <Tab
                  label="Closing"
                  icon={<Handshake />}
                  iconPosition="start"
                />
                {contract?.contingency?.inspectionContingency === 'yes' && (
                  <Tab
                    label="Inspection & Property"
                    icon={<Home />}
                    iconPosition="start"
                  />
                )}
                <Tab label="Addendums" icon={<Gavel />} iconPosition="start" />
              </Tabs>
            </Box>

            {/* Tab Panels */}
            <CustomTabPanel value={activeTab} index={0}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                Transaction Timeline
              </Typography>
              <Stepper
                orientation="vertical"
                sx={{
                  '& .MuiStepLabel-label': {
                    color: 'text.primary',
                  },
                  '& .MuiStepLabel-label.Mui-disabled': {
                    color: 'text.primary',
                    opacity: 0.9,
                  },
                }}
              >
                {timelineEvents.map((event, index) => {
                  const daysUntil = getDaysUntilDeadline(event.dueDate);
                  const isOverdue = event.status === 'overdue';
                  const isInProgress = event.status === 'in_progress';

                  return (
                    <Step
                      key={event.id}
                      active={!isOverdue}
                      completed={event.status === 'completed'}
                    >
                      <StepLabel
                        icon={
                          <Avatar
                            sx={{
                              bgcolor: isOverdue
                                ? theme.palette.error.main
                                : isInProgress
                                ? theme.palette.warning.main
                                : theme.palette.mainColors.primary,
                              width: 32,
                              height: 32,
                            }}
                          >
                            {event.type === 'inspection' && <Build />}
                            {event.type === 'financing' && <CreditCard />}
                            {event.type === 'title' && <VerifiedUser />}
                            {event.type === 'closing' && <AccountBalance />}
                            {![
                              'inspection',
                              'financing',
                              'title',
                              'closing',
                            ].includes(event.type) && <Event />}
                          </Avatar>
                        }
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ color: 'text.primary' }}
                          >
                            {event.title}
                          </Typography>
                          <Chip
                            label={event.status.replace('_', ' ')}
                            color={
                              isOverdue
                                ? 'error'
                                : isInProgress
                                ? 'warning'
                                : 'default'
                            }
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          <Chip
                            label={event.assignedTo}
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                          {event.extensionDays && (
                            <Chip
                              label={`+${event.extensionDays} days`}
                              color="info"
                              size="small"
                              icon={<Extension />}
                            />
                          )}
                        </Box>
                      </StepLabel>
                      <StepContent>
                        <Typography
                          variant="body2"
                          sx={{ mb: 2, color: 'text.secondary' }}
                        >
                          {event.description}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                          >
                            Due: {formatTimelineDate(event.dueDate)}
                          </Typography>
                          {daysUntil > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.mainColors.green }}
                            >
                              {daysUntil} days remaining
                            </Typography>
                          )}
                          {isOverdue && (
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.error.main }}
                            >
                              {Math.abs(daysUntil)} days overdue
                            </Typography>
                          )}
                        </Box>
                        {event.extensionReason && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.greyShades.grey2,
                              fontStyle: 'italic',
                            }}
                          >
                            Extension reason: {event.extensionReason}
                          </Typography>
                        )}
                        <Box mt={2}>
                          <GlassButton size="small" startIcon={<Visibility />}>
                            View Details
                          </GlassButton>
                          {event.assignedTo === userRole &&
                            event.status !== 'completed' && (
                              <GlassButton
                                size="small"
                                startIcon={<Edit />}
                                sx={{ ml: 1 }}
                              >
                                Update Status
                              </GlassButton>
                            )}
                        </Box>
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </CustomTabPanel>

            <CustomTabPanel value={activeTab} index={1}>
              <TaskManagement userRole={userRole} contractId={contractId} />
            </CustomTabPanel>

            <CustomTabPanel value={activeTab} index={2}>
              <DocumentCenter contractId={contractId} userRole={userRole} />
            </CustomTabPanel>

            <CustomTabPanel value={activeTab} index={3}>
              <CommunicationHub
                contractId={contractId}
                userRole={userRole}
                userName={userName}
              />
            </CustomTabPanel>

            <CustomTabPanel value={activeTab} index={4}>
              <ClosingCoordination
                contractId={contractId}
                userRole={userRole}
              />
            </CustomTabPanel>

            {contract?.contingency?.inspectionContingency === 'yes' && (
              <CustomTabPanel value={activeTab} index={5}>
                <InspectionProperty
                  contractId={contractId}
                  userRole={userRole}
                  userName={userName}
                />
              </CustomTabPanel>
            )}

            <CustomTabPanel
              value={activeTab}
              index={
                contract?.contingency?.inspectionContingency === 'yes' ? 6 : 5
              }
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: theme.palette.mainColors.primary }}
              >
                Addendums ({addendumsData?.data?.length || 0})
              </Typography>
              {addendumsData?.data && addendumsData.data.length > 0 ? (
                <List>
                  {addendumsData.data.map((addendum: any) => (
                    <ListItem key={addendum._id} divider>
                      <ListItemIcon>
                        <Description
                          sx={{ color: theme.palette.mainColors.primary }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>{addendum.title}</span>
                            <Chip
                              label={addendum.status}
                              size="small"
                              color={
                                addendum.status === 'signed'
                                  ? 'success'
                                  : 'default'
                              }
                            />
                          </Box>
                        }
                        secondary={`Type: ${addendum.type} | Created by: ${addendum.createdBy}`}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => setViewedAddendumId(addendum._id)}
                      >
                        View
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  No addendums found for this contract. Click the + button to
                  create one.
                </Alert>
              )}
            </CustomTabPanel>
          </GlassCard>

          {/* Extension Request Dialog */}
          <Dialog
            open={extensionDialogOpen}
            onClose={() => setExtensionDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Request Contract Extension</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Reason for Extension"
                fullWidth
                multiline
                rows={3}
                value={extensionReason}
                onChange={(e) => setExtensionReason(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Requested Days"
                type="number"
                fullWidth
                value={requestedDays}
                onChange={(e) => setRequestedDays(Number(e.target.value))}
                inputProps={{ min: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExtensionDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleExtensionRequest} variant="contained">
                Submit Request
              </Button>
            </DialogActions>
          </Dialog>

          {/* Message Dialog */}
          <Dialog
            open={messageDialogOpen}
            onClose={() => setMessageDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Send Message</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Message"
                fullWidth
                multiline
                rows={4}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMessageDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendMessage} variant="contained">
                Send Message
              </Button>
            </DialogActions>
          </Dialog>

          {/* View Addendum Dialog */}
          <Dialog
            open={Boolean(viewedAddendumId)}
            onClose={() => setViewedAddendumId(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Addendum details</DialogTitle>
            <DialogContent>
              {(() => {
                const addendum = (addendumsData?.data || []).find(
                  (a: { _id: string }) => a._id === viewedAddendumId
                );
                if (!addendum) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Addendum not found.
                    </Typography>
                  );
                }
                return (
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <Typography variant="subtitle1">
                      {addendum.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type: {addendum.type} · Status: {addendum.status}
                    </Typography>
                    <Typography variant="body2">
                      {addendum.description || 'No description'}
                    </Typography>
                  </Stack>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewedAddendumId(null)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Create Addendum Dialog */}
          <CreateAddendumForm
            open={addendumDialogOpen}
            onClose={() => setAddendumDialogOpen(false)}
            contractId={contractId}
            userRole={userRole}
          />
        </Box>
      </Slide>
    </Container>
  );
};

export default AgentPropertyDashboard;
